import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks/use-toast';
import { drawSymbolShape } from './canvas/symbol-renderer';
import { renderPaths } from './canvas/path-renderer';
import { BUFFER_SIZE } from './canvas/types';
import { useMapCamera } from './hooks/use-map-camera';
import { useMapDrawing } from './hooks/use-map-drawing';
import { useAreaCanvas } from './canvas/use-area-canvas';
import { usePathCanvas } from './canvas/use-path-canvas';
import { useLabelCanvas } from './canvas/use-label-canvas';
import { useCursorPreview } from './canvas/use-cursor-preview';
import { useGridCanvas } from './canvas/use-grid-canvas';
import { useRiverSnap } from './canvas/use-river-snap';
import { useSymbolInteraction } from './canvas/use-symbol-interaction';
import { useCanvasInteraction } from './canvas/use-canvas-interaction';
import { useNovel } from '@/context/novel-context';

import type {
    DrawingTool, LandTool, MapCanvasHandle, MapCanvasProps, MapSection,
    PlacedSymbol, SymbolMode, MapEntityPoint, MapEntityArea, MapEntityRef,
    MapAreaStyle, MapEntityPath, MapPathStyle,
} from './canvas/types';

export type {
    DrawingTool, LandTool, MapCanvasHandle, MapCanvasProps, MapSection,
    SymbolMode, MapEntityPoint, MapEntityArea, MapEntityRef, MapAreaStyle,
    MapEntityPath, MapPathStyle,
};

export const MapCanvas = forwardRef<MapCanvasHandle, MapCanvasProps>((props, ref) => {
    const {
        location,
        activeLandTool, paintOpacity, brush, brushSize, roughness, onCommit,
        tool, activeSection, activeSymbol, activeSymbols = [],
        setActiveSymbol, activeSymbolCategory,
        symbolMode = 'single', symbolSize = 20, stampDensity = 0.3,
        placedSymbols = [], setPlacedSymbols,
        // Asset hoisting
        isAssetsLoading, lastUpdated, landRef, colorRef, symbolsRef,
        assemblerRef, patternsRef, landMaskRef, stampMaskRef,
        processedSheets, customMapImageRef, historyTick,
        // World entities
        mapEntityPoints = [], setMapEntityPoints,
        mapEntityAreas = [], setMapEntityAreas,
        isPlacingPoint = false, isDrawingArea = false,
        onPointPlaced, selectedWorldUid, setSelectedWorldUid,
        inProgressVertices = [], cursorVertex = null,
        setCursorVertex, addVertex, finishDrawing, onAreaDrawn,
        // Paths
        mapEntityPaths = [], setMapEntityPaths,
        isDrawingPath = false, selectedPathUid, setSelectedPathUid,
        inProgressPathVertices = [], pathCursorVertex = null,
        setPathCursorVertex, addPathVertex, finishPathDrawing,
        movePathVertex, insertPathVertex, deletePathVertex, activePathTypeRef,
        // Labels
        mapLabels = [], setMapLabels,
        selectedLabelUid, setSelectedLabelUid, hoveredLabelUid,
        // Layers
        layerVisibility,
    } = props;

    const isCustomUpload = !!location?.originalMapUrl;
    const isCustom = isCustomUpload && !!customMapImageRef?.current;
    const mapWidth = (isCustom && customMapImageRef?.current) ? customMapImageRef.current.naturalWidth : BUFFER_SIZE;
    const mapHeight = (isCustom && customMapImageRef?.current) ? customMapImageRef.current.naturalHeight : BUFFER_SIZE;

    // Canvas layer refs
    const bgCanvasRef = useRef<HTMLCanvasElement>(null);
    const midCanvasRef = useRef<HTMLCanvasElement>(null);
    const areaCanvasRef = useRef<HTMLCanvasElement>(null);
    const pathCanvasRef = useRef<HTMLCanvasElement>(null);
    const labelCanvasRef = useRef<HTMLCanvasElement>(null);
    const gridCanvasRef = useRef<HTMLCanvasElement>(null);
    const fgCanvasRef = useRef<HTMLCanvasElement>(null);

    // Hover state
    const [hoveredWorldUid, setHoveredWorldUid] = useState<string | null>(null);
    const [hoveredPathUid, setHoveredPathUid] = useState<string | null>(null);

    // Offscreen caches
    const bgBufferRef = useRef<HTMLCanvasElement | null>(null);
    const midBufferRef = useRef<HTMLCanvasElement | null>(null);

    // Interaction refs
    const containerRef = useRef<HTMLDivElement>(null);
    const mousePos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const lastPanPos = useRef<{ x: number; y: number } | null>(null);
    const selectedSymbolUid = useRef<string | null>(null);
    const dragOffset = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
    const lastPlacementPos = useRef<{ x: number; y: number } | null>(null);
    const draggingVertex = useRef<{ pathUid: string; index: number } | null>(null);

    // Painting optimisation
    const brushQueueRef = useRef<{ x: number; y: number; lx: number; ly: number }[]>([]);
    const dirtyBgRectRef = useRef<{ minX: number; minY: number; maxX: number; maxY: number }>({
        minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity,
    });

    // Dirty flags
    const dirtyBgCache = useRef(true);
    const dirtyMidCache = useRef(true);
    const dirtyBgScreen = useRef(true);
    const dirtyMidScreen = useRef(true);
    const dirtyFg = useRef(true);

    const [isPainting, setIsPainting] = useState(false);
    const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(null);
    const [isHovering, setIsHovering] = useState(false);
    const [isPanning, setIsPanning] = useState(false);
    const [dimensions, setDimensions] = useState({ width: 800, height: 800 });
    const [activeSelectedUid, setActiveSelectedUid] = useState<string | null>(null);

    const { novel } = useNovel();
    const locationSummaries = novel?.summaries?.locations || {};
    const eventSummaries = novel?.summaries?.events || {};
    
    const resolveEntityName = useCallback((ref: MapEntityRef) => {
        if (ref.type === 'locations') return locationSummaries[ref.id]?.name || ref.name;
        if (ref.type === 'events') return eventSummaries[ref.id]?.name || ref.name;
        return ref.name;
    }, [locationSummaries, eventSummaries]);

    const [cameraTick, setCameraTick] = useState(0);
    const triggerCameraUpdate = useCallback(() => setCameraTick(t => t + 1), []);

    useEffect(() => { setActiveSelectedUid(null); }, [symbolMode, activeSection]);

    const { zoomRef, panRef, resetView, zoomIn, zoomOut, updatePan, updateZoom } = useMapCamera(dimensions, mapWidth, mapHeight);
    const { lastPos, handlePaint } = useMapDrawing(landRef, colorRef, symbolsRef, landMaskRef, stampMaskRef);

    const { t } = useTranslation();
    const { toast } = useToast();

    // ── Sub-hooks ────────────────────────────────────────────────────────────
    const { snapRiverNode } = useRiverSnap({ landMaskRef, mapEntityPaths });

    const { eraseSymbolsAt, spraySymbolsAt, sprayStampsAt } = useSymbolInteraction({
        mapWidth, mapHeight, brushSize, symbolSize, stampDensity,
        activeSymbol, activeSymbols, placedSymbols,
        landMaskRef, lastPlacementPos, setPlacedSymbols,
    });

    // ── Canvas drawing effects ────────────────────────────────────────────────
    useAreaCanvas({
        canvasRef: areaCanvasRef, mapEntityAreas, inProgressVertices,
        cursorVertex, isDrawingArea, dimensions, zoomRef, panRef,
        selectedWorldUid, hoveredWorldUid, isCustomUpload, customMapImageRef, cameraTick,
        layerVisibility, activeSection, resolveEntityName,
    });

    usePathCanvas({
        canvasRef: pathCanvasRef, mapEntityPaths, inProgressPathVertices,
        pathCursorVertex, isDrawingPath, dimensions, zoomRef, panRef,
        selectedPathUid: selectedPathUid ?? null, hoveredPathUid,
        isCustomUpload, customMapImageRef, cameraTick, placedSymbols,
        layerVisibility, activeSection,
    });

    useLabelCanvas({
        canvasRef: labelCanvasRef, mapLabels, selectedLabelUid, hoveredLabelUid: hoveredLabelUid ?? null,
        dimensions, zoomRef, panRef, isCustomUpload, customMapImageRef, cameraTick,
    });

    useGridCanvas({
        canvasRef: gridCanvasRef, dimensions, zoomRef, panRef,
        isCustomUpload, customMapImageRef, cameraTick, layerVisibility,
    });

    useCursorPreview({
        fgCanvasRef, cursorPos, isHovering, activeSection, symbolMode, brushSize,
        activeSymbol, activeLandTool, processedSheets, symbolSize, dimensions,
        placedSymbols, activeSelectedUid, zoomRef, panRef, isCustomUpload, customMapImageRef,
    });

    // ── Canvas interaction ───────────────────────────────────────────────────
    const drawOptions = {
        activeSection, activeLandTool, tool, brush, brushSize,
        paintOpacity, activeSymbol, activeSymbols, processedSheets,
        symbolMode: symbolMode === 'select' ? undefined : symbolMode,
        stampDensity, isNatural: symbolMode === 'brush',
        symbolSize, roughness,
    };

    const { onEvent, onWheel } = useCanvasInteraction({
        dimensions, isCustomUpload, customMapImageRef, zoomRef, panRef,
        activeSection, activeLandTool, symbolMode, brushSize, symbolSize, activeSymbol,
        isPlacingPoint, isDrawingArea, mapEntityAreas, selectedWorldUid, inProgressVertices,
        inProgressPathVertices, isDrawingPath, mapEntityPaths, selectedPathUid: selectedPathUid ?? null,
        activePathTypeRef, placedSymbols, activeSelectedUid,
        mapLabels, selectedLabelUid: selectedLabelUid ?? null,
        lastPanPos, lastPos, selectedSymbolUid, dragOffset, lastPlacementPos, draggingVertex, brushQueueRef,
        isPainting, isPanning, setIsPainting, setIsPanning,
        setCursorPos, setCursorVertex, setPathCursorVertex,
        setHoveredWorldUid, setHoveredPathUid, setActiveSelectedUid,
        setSelectedWorldUid, setSelectedPathUid: (uid) => setSelectedPathUid(uid ?? null),
        setSelectedLabelUid: (uid) => setSelectedLabelUid(uid ?? null),
        setHoveredLabelUid: (uid) => { /* currently handled internally if we want, or pass it */ },
        addLabel: (x, y, text, template) => {
            const uid = Math.random().toString(36).substring(2, 9);
            setMapLabels(prev => [...prev, {
                uid, x, y, text: text || 'New Label', rotation: 0,
                fontFamily: template?.fontFamily ?? 'Cinzel',
                fontSize: template?.fontSize ?? 24,
                color: template?.color ?? '#000000',
                outlineColor: template?.outlineColor ?? '#ffffff',
                outlineWidth: template?.outlineWidth ?? 0,
                fontWeight: template?.fontWeight ?? 'normal',
                fontStyle: template?.fontStyle ?? 'normal',
            }]);
            setSelectedLabelUid(uid);
        },
        updateLabel: (uid, updates) => {
            setMapLabels(prev => prev.map(l => l.uid === uid ? { ...l, ...updates } : l));
        },
        setPlacedSymbols, onPointPlaced, addVertex, addPathVertex,
        insertPathVertex, movePathVertex, finishDrawing, finishPathDrawing, onCommit,
        eraseSymbolsAt, spraySymbolsAt, sprayStampsAt, snapRiverNode,
        updatePan, updateZoom,
        dirtyBgScreen, dirtyMidScreen, dirtyFg, triggerCameraUpdate,
        fgCanvasRef, brush, activeSymbols,
        toast: (opts) => toast({ title: opts.title, description: opts.description, variant: opts.variant === 'destructive' ? 'destructive' : 'default' }),
        tLimitReached: t('map_creator.symbol_panel.limit_reached'),
        tLimitDesc: t('map_creator.symbol_panel.limit_desc'),
    });

    // ── Symbol keyboard handler ───────────────────────────────────────────────
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (activeSection !== 'symbols' || symbolMode !== 'select' || !activeSelectedUid) return;
            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                setPlacedSymbols(prev => prev.filter(sym => sym.uid !== activeSelectedUid));
                setActiveSelectedUid(null);
                onCommit();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                setActiveSelectedUid(null);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [activeSection, symbolMode, activeSelectedUid, setPlacedSymbols, onCommit]);

    // ── Dirty flag: symbols changed ───────────────────────────────────────────
    useEffect(() => {
        dirtyMidCache.current = true;
        dirtyMidScreen.current = true;
        dirtyFg.current = true;
    }, [placedSymbols]);

    // ── Init offscreen buffers ────────────────────────────────────────────────
    useEffect(() => {
        const create = () => {
            const c = document.createElement('canvas');
            c.width = BUFFER_SIZE; c.height = BUFFER_SIZE;
            return c;
        };
        if (!bgBufferRef.current) bgBufferRef.current = create();
        if (!midBufferRef.current) midBufferRef.current = create();
        if (areaCanvasRef.current) {
            areaCanvasRef.current.width = BUFFER_SIZE;
            areaCanvasRef.current.height = BUFFER_SIZE;
        }
    }, []);

    // ── Force redraw when assets load ─────────────────────────────────────────
    useEffect(() => {
        if (!isAssetsLoading) {
            if (isCustomUpload && customMapImageRef?.current) {
                const w = customMapImageRef.current.naturalWidth;
                const h = customMapImageRef.current.naturalHeight;
                if (bgBufferRef.current) { bgBufferRef.current.width = w; bgBufferRef.current.height = h; }
                if (midBufferRef.current) { midBufferRef.current.width = w; midBufferRef.current.height = h; }
            }
            dirtyBgCache.current = true; dirtyMidCache.current = true;
            dirtyBgScreen.current = true; dirtyMidScreen.current = true;
            dirtyFg.current = true;
        }
    }, [isAssetsLoading, lastUpdated, isCustomUpload, customMapImageRef]);

    // ── Force redraw when history is restored ─────────────────────────────────
    useEffect(() => {
        if (historyTick !== undefined && historyTick > 0) {
            dirtyBgCache.current = true; dirtyMidCache.current = true;
            dirtyBgScreen.current = true; dirtyMidScreen.current = true;
            dirtyFg.current = true;
        }
    }, [historyTick]);

    // ── Responsive sizing ─────────────────────────────────────────────────────
    const updateSize = useCallback(() => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const w = Math.floor(rect.width);
        const h = Math.floor(rect.height);
        if (w < 10 || h < 10) return;
        const dpr = window.devicePixelRatio || 1;
        const setupCanvas = (canvas: HTMLCanvasElement | null) => {
            if (!canvas) return;
            canvas.width = w * dpr; canvas.height = h * dpr;
            canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
            canvas.getContext('2d')?.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        setupCanvas(bgCanvasRef.current);
        setupCanvas(midCanvasRef.current);
        setupCanvas(fgCanvasRef.current);
        setDimensions(prev => (prev.width === w && prev.height === h) ? prev : { width: w, height: h });
        dirtyBgScreen.current = true; dirtyMidScreen.current = true; dirtyFg.current = true;
    }, []);

    useEffect(() => {
        if (isAssetsLoading) return;
        updateSize();
        const obs = new ResizeObserver(updateSize);
        if (containerRef.current) obs.observe(containerRef.current);
        window.addEventListener('resize', updateSize);
        return () => { obs.disconnect(); window.removeEventListener('resize', updateSize); };
    }, [updateSize, isAssetsLoading]);

    useEffect(() => { triggerCameraUpdate(); }, [dimensions, triggerCameraUpdate]);

    // ── Main render loop ──────────────────────────────────────────────────────
    const render = useCallback(() => {
        if (!landRef.current || !colorRef.current || !assemblerRef.current || !landMaskRef.current) return;

        // Drain brush queue
        if (brushQueueRef.current.length > 0) {
            const queue = brushQueueRef.current;
            brushQueueRef.current = [];
            const rect = dirtyBgRectRef.current;
            for (const pt of queue) handlePaint(pt.x, pt.y, pt.lx, pt.ly, drawOptions, rect);

            if (rect.minX <= rect.maxX) {
                const ctx = landRef.current.getContext('2d', { alpha: true })!;
                const minX = Math.max(0, Math.floor(rect.minX));
                const minY = Math.max(0, Math.floor(rect.minY));
                const maxX = Math.min(BUFFER_SIZE - 1, Math.ceil(rect.maxX));
                const maxY = Math.min(BUFFER_SIZE - 1, Math.ceil(rect.maxY));
                const TILE_SIZE = 256;
                const mask = landMaskRef.current;
                for (let ty = Math.floor(minY / TILE_SIZE) * TILE_SIZE; ty <= maxY; ty += TILE_SIZE) {
                    for (let tx = Math.floor(minX / TILE_SIZE) * TILE_SIZE; tx <= maxX; tx += TILE_SIZE) {
                        const curMinX = Math.max(minX, tx); const curMaxX = Math.min(maxX, tx + TILE_SIZE - 1);
                        const curMinY = Math.max(minY, ty); const curMaxY = Math.min(maxY, ty + TILE_SIZE - 1);
                        const w = curMaxX - curMinX + 1; const h = curMaxY - curMinY + 1;
                        if (w <= 0 || h <= 0) continue;
                        const imgData = ctx.createImageData(w, h);
                        for (let y = 0; y < h; y++) {
                            for (let x = 0; x < w; x++) {
                                const maskIdx = (curMinY + y) * BUFFER_SIZE + (curMinX + x);
                                const imgIdx = (y * w + x) * 4;
                                const isLand = mask[maskIdx] > 128;
                                imgData.data[imgIdx] = 255; imgData.data[imgIdx + 1] = 255;
                                imgData.data[imgIdx + 2] = 255; imgData.data[imgIdx + 3] = isLand ? 255 : 0;
                            }
                        }
                        ctx.putImageData(imgData, curMinX, curMinY);
                    }
                }
                dirtyBgCache.current = true;
                rect.minX = Infinity; rect.minY = Infinity; rect.maxX = -Infinity; rect.maxY = -Infinity;
            }
        }

        const { width, height } = dimensions;
        const isCustom2 = isCustomUpload && !!customMapImageRef?.current;
        const mW = isCustom2 ? (customMapImageRef?.current?.naturalWidth ?? BUFFER_SIZE) : BUFFER_SIZE;
        const mH = isCustom2 ? (customMapImageRef?.current?.naturalHeight ?? BUFFER_SIZE) : BUFFER_SIZE;
        let rW = Math.min(width, height); let rH = Math.min(width, height);
        if (isCustom2) {
            const ratio = mW / mH;
            if (width / height > ratio) { rH = height; rW = height * ratio; }
            else { rW = width; rH = width / ratio; }
        }
        const zoom = zoomRef.current;
        const pan = panRef.current;

        // BG cache
        const bgBuffer = bgBufferRef.current;
        if (dirtyBgCache.current && bgBuffer) {
            const ctx = bgBuffer.getContext('2d', { alpha: false });
            if (!ctx) return;
            ctx.clearRect(0, 0, mW, mH);
            if (isCustomUpload) {
                if (customMapImageRef?.current) {
                    ctx.drawImage(customMapImageRef.current, 0, 0, mW, mH);
                } else {
                    ctx.fillStyle = '#1e1e1e'; ctx.fillRect(0, 0, mW, mH);
                }
                ctx.save(); ctx.globalAlpha = paintOpacity * 0.45;
                ctx.drawImage(colorRef.current, 0, 0); ctx.restore();
            } else {
                ctx.fillStyle = '#43646d'; ctx.fillRect(0, 0, BUFFER_SIZE, BUFFER_SIZE);
                const waterPat = patternsRef.current?.water;
                if (waterPat) { ctx.save(); ctx.globalAlpha = 0.5; ctx.fillStyle = waterPat; ctx.fillRect(0, 0, BUFFER_SIZE, BUFFER_SIZE); ctx.restore(); }
                ctx.save(); ctx.shadowColor = '#87a8ad'; ctx.shadowBlur = 8; ctx.globalAlpha = 0.3;
                ctx.drawImage(landRef.current, 0, 0, BUFFER_SIZE, BUFFER_SIZE); ctx.restore();
                const off = assemblerRef.current;
                const octx = off.getContext('2d')!;
                octx.clearRect(0, 0, BUFFER_SIZE, BUFFER_SIZE);
                octx.fillStyle = patternsRef.current?.land || '#d4c59b';
                octx.fillRect(0, 0, BUFFER_SIZE, BUFFER_SIZE);
                octx.save(); octx.globalAlpha = paintOpacity * 0.45; octx.drawImage(colorRef.current, 0, 0); octx.restore();
                octx.globalCompositeOperation = 'destination-in'; octx.drawImage(landRef.current, 0, 0);
                octx.globalCompositeOperation = 'source-over';
                ctx.shadowColor = 'rgba(0, 0, 0, 0.2)'; ctx.shadowBlur = 8;
                ctx.drawImage(off, 0, 0, BUFFER_SIZE, BUFFER_SIZE); ctx.shadowBlur = 0;
                // Procedural cliff generation
                try {
                    const mountMask = document.createElement('canvas');
                    mountMask.width = BUFFER_SIZE; mountMask.height = BUFFER_SIZE;
                    const mctx = mountMask.getContext('2d')!;
                    const colorCtx = colorRef.current.getContext('2d')!;
                    const imgData = colorCtx.getImageData(0, 0, BUFFER_SIZE, BUFFER_SIZE);
                    const data = imgData.data;
                    const maskImgData = mctx.createImageData(BUFFER_SIZE, BUFFER_SIZE);
                    const mdata = maskImgData.data;
                    let hasMountainPixels = false;
                    for (let i = 0; i < data.length; i += 4) {
                        if (data[i + 3] > 30) {
                            const r = data[i]; const g = data[i + 1]; const b = data[i + 2];
                            if (Math.abs(r - 90) < 18 && Math.abs(g - 101) < 18 && Math.abs(b - 117) < 18) {
                                mdata[i] = 255; mdata[i + 1] = 255; mdata[i + 2] = 255; mdata[i + 3] = 255;
                                hasMountainPixels = true;
                            }
                        }
                    }
                    if (hasMountainPixels) {
                        mctx.putImageData(maskImgData, 0, 0);
                        const thickOutline = document.createElement('canvas');
                        thickOutline.width = BUFFER_SIZE; thickOutline.height = BUFFER_SIZE;
                        const toctx = thickOutline.getContext('2d')!;
                        const radius = 6;
                        for (let dx = -radius; dx <= radius; dx += 3)
                            for (let dy = -radius; dy <= radius; dy += 3)
                                if (dx * dx + dy * dy <= radius * radius) toctx.drawImage(landRef.current, dx, dy);
                        toctx.globalCompositeOperation = 'destination-out'; toctx.drawImage(landRef.current, 0, 0);
                        toctx.globalCompositeOperation = 'destination-in'; toctx.drawImage(mountMask, 0, 0);
                        toctx.globalCompositeOperation = 'source-over';
                        const hatchCanvas = document.createElement('canvas');
                        hatchCanvas.width = BUFFER_SIZE; hatchCanvas.height = BUFFER_SIZE;
                        const hctx = hatchCanvas.getContext('2d')!;
                        hctx.strokeStyle = '#2f353d'; hctx.lineWidth = 1.5; hctx.beginPath();
                        for (let k = -BUFFER_SIZE; k < BUFFER_SIZE * 2; k += 6) { hctx.moveTo(k, 0); hctx.lineTo(k + BUFFER_SIZE * 0.5, BUFFER_SIZE); }
                        hctx.stroke(); hctx.globalCompositeOperation = 'destination-in'; hctx.drawImage(thickOutline, 0, 0);
                        const thinOutline = document.createElement('canvas');
                        thinOutline.width = BUFFER_SIZE; thinOutline.height = BUFFER_SIZE;
                        const thinctx = thinOutline.getContext('2d')!;
                        for (let dx = -2; dx <= 2; dx++) for (let dy = -2; dy <= 2; dy++) thinctx.drawImage(landRef.current, dx, dy);
                        thinctx.globalCompositeOperation = 'destination-out'; thinctx.drawImage(landRef.current, 0, 0);
                        thinctx.globalCompositeOperation = 'destination-in'; thinctx.drawImage(mountMask, 0, 0);
                        thinctx.globalCompositeOperation = 'source-in'; thinctx.fillStyle = '#1f242b';
                        thinctx.fillRect(0, 0, BUFFER_SIZE, BUFFER_SIZE);
                        ctx.drawImage(hatchCanvas, 0, 0); ctx.drawImage(thinOutline, 0, 0);
                    }
                } catch (e) { console.warn('Failed to generate mountainous cliffs:', e); }
                ctx.globalAlpha = 0.12; ctx.drawImage(landRef.current, 0, 0, BUFFER_SIZE, BUFFER_SIZE); ctx.globalAlpha = 1.0;
            }
            dirtyBgCache.current = false; dirtyBgScreen.current = true;
        }

        // BG screen blit
        if (dirtyBgScreen.current && bgCanvasRef.current && bgBuffer) {
            const ctx = bgCanvasRef.current.getContext('2d', { alpha: false });
            if (ctx) {
                ctx.clearRect(0, 0, width, height);
                ctx.save(); ctx.translate(pan.x, pan.y); ctx.scale(zoom, zoom);
                ctx.drawImage(bgBuffer, 0, 0, rW, rH); ctx.restore();
                dirtyBgScreen.current = false;
            }
        }

        // Mid cache
        const midBuffer = midBufferRef.current;
        if (dirtyMidCache.current && midBuffer) {
            const ctx = midBuffer.getContext('2d');
            if (!ctx) return;
            ctx.clearRect(0, 0, mW, mH);
            ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
            const sortedSymbols = [...placedSymbols].sort((a, b) => a.y - b.y);
            for (const sym of sortedSymbols) drawSymbolShape(ctx, sym.id, sym.x, sym.y, sym.size, processedSheets);
            ctx.strokeStyle = 'rgba(255,255,255,0.1)'; ctx.lineWidth = 1; ctx.strokeRect(0, 0, mW, mH);
            dirtyMidCache.current = false; dirtyMidScreen.current = true;
        }

        // Mid screen blit
        if (dirtyMidScreen.current && midCanvasRef.current) {
            const ctx = midCanvasRef.current.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, width, height);
                ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
                const sortedSymbols = [...placedSymbols].sort((a, b) => a.y - b.y);
                for (const sym of sortedSymbols) {
                    const screenX = (sym.x / mW) * rW * zoom + pan.x;
                    const screenY = (sym.y / mH) * rH * zoom + pan.y;
                    const screenSize = sym.size * (rW / mW) * zoom;
                    const half = screenSize / 2;
                    if (screenX + half < 0 || screenX - half > width || screenY + half < 0 || screenY - half > height) continue;
                    drawSymbolShape(ctx, sym.id, screenX, screenY, screenSize, processedSheets);
                }
                dirtyMidScreen.current = false;
            }
        }
    }, [dimensions, isAssetsLoading, lastUpdated, patternsRef, assemblerRef, processedSheets,
        activeSection, activeSymbol, landRef, colorRef, symbolsRef, brushSize, isPainting,
        zoomRef, panRef, landMaskRef, drawOptions, handlePaint, isCustomUpload, customMapImageRef]);

    useEffect(() => {
        let frameId: number;
        const loop = () => { render(); frameId = requestAnimationFrame(loop); };
        frameId = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(frameId);
    }, [render]);

    // ── Imperative handle ─────────────────────────────────────────────────────
    useImperativeHandle(ref, () => ({
        getSchematic: () => bgCanvasRef.current?.toDataURL('image/png') || '',
        getBuffers: async () => {
            const sCtx = symbolsRef.current?.getContext('2d');
            if (sCtx) {
                sCtx.clearRect(0, 0, mapWidth, mapHeight);
                sCtx.imageSmoothingEnabled = true; sCtx.imageSmoothingQuality = 'high';
                [...placedSymbols].sort((a, b) => a.y - b.y).forEach(sym =>
                    drawSymbolShape(sCtx, sym.id, sym.x, sym.y, sym.size, processedSheets),
                );
            }
            const landBlob = await new Promise<Blob | null>(r => landRef.current?.toBlob(r, 'image/webp', 0.90));
            const colorBlob = await new Promise<Blob | null>(r => colorRef.current?.toBlob(r, 'image/webp', 0.90));
            const symbolsBlob = await new Promise<Blob | null>(r => symbolsRef.current?.toBlob(r, 'image/webp', 0.90));
            return { land: landBlob, color: colorBlob, symbols: symbolsBlob };
        },
        getComposite: async () => {
            const bgBuffer = bgBufferRef.current;
            const midBuffer = midBufferRef.current;
            if (!bgBuffer || !midBuffer) return null;
            const canvas = document.createElement('canvas');
            canvas.width = mapWidth; canvas.height = mapHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) return null;
            ctx.drawImage(bgBuffer, 0, 0); ctx.drawImage(midBuffer, 0, 0);

            // Draw paths/rivers directly onto the composite image
            const toScreenIdentity = (x: number, y: number) => ({ sx: x, sy: y });
            renderPaths(ctx, mapEntityPaths, toScreenIdentity, {
                selectedUid: null,
                hoveredUid: null,
                zoom: 1.0,
                placedSymbols: placedSymbols,
            });

            // Draw labels directly onto the composite image
            for (const label of mapLabels) {
                ctx.save();
                ctx.translate(label.x, label.y);
                if (label.rotation) {
                    ctx.rotate(label.rotation);
                }

                const fontString = `${label.fontStyle === 'italic' ? 'italic' : 'normal'} ${label.fontWeight === 'bold' ? 'bold' : 'normal'} ${label.fontSize}px ${label.fontFamily}`;
                
                ctx.font = fontString;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.lineJoin = 'round';
                ctx.miterLimit = 2;
                
                if (label.outlineWidth > 0) {
                    ctx.strokeStyle = label.outlineColor;
                    ctx.lineWidth = label.outlineWidth;
                    ctx.strokeText(label.text, 0, 0);
                }

                ctx.fillStyle = label.color;
                ctx.fillText(label.text, 0, 0);

                ctx.restore();
            }

            return new Promise<Blob | null>(r => canvas.toBlob(r, 'image/webp', 0.90));
        },
        resetView: () => { resetView(); dirtyBgScreen.current = true; dirtyMidScreen.current = true; dirtyFg.current = true; triggerCameraUpdate(); },
        zoomIn: () => { zoomIn(); dirtyBgScreen.current = true; dirtyMidScreen.current = true; dirtyFg.current = true; triggerCameraUpdate(); },
        zoomOut: () => { zoomOut(); dirtyBgScreen.current = true; dirtyMidScreen.current = true; dirtyFg.current = true; triggerCameraUpdate(); },
    }), [resetView, zoomIn, zoomOut, landRef, colorRef, symbolsRef, triggerCameraUpdate, mapEntityPaths, placedSymbols]);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const getCursorStyle = useCallback(() => {
        if (!isHovering) return 'default';
        if (isPanning) return 'grabbing';
        if (isPlacingPoint || isDrawingArea || isDrawingPath) return 'crosshair';
        if (activeSection === 'world' && hoveredWorldUid) return 'pointer';
        if (activeSection === 'paths' && hoveredPathUid) return 'pointer';
        if (activeSection === 'symbols' && symbolMode === 'select') return 'crosshair';
        const hasCustomCursor =
            (activeSection === 'symbols' && (symbolMode === 'brush' || symbolMode === 'eraser' || symbolMode === 'single')) ||
            (activeSection === 'land' && (activeLandTool === 'raise' || activeLandTool === 'sink' || activeLandTool === 'paint' || activeLandTool === 'stamp'));
        return hasCustomCursor ? 'none' : 'default';
    }, [isHovering, isPanning, isPlacingPoint, isDrawingArea, isDrawingPath, activeSection, hoveredWorldUid, hoveredPathUid, symbolMode, activeLandTool]);

    const pointToScreen = useCallback((px: number, py: number) => {
        const isCustom2 = isCustomUpload && !!customMapImageRef?.current;
        const mW = isCustom2 ? customMapImageRef!.current!.naturalWidth : BUFFER_SIZE;
        const mH = isCustom2 ? customMapImageRef!.current!.naturalHeight : BUFFER_SIZE;
        const { width, height } = dimensions;
        let rW = Math.min(width, height); let rH = Math.min(width, height);
        if (isCustom2) {
            const ratio = mW / mH;
            if (width / height > ratio) { rH = height; rW = height * ratio; } else { rW = width; rH = width / ratio; }
        }
        const zoom = zoomRef.current; const pan = panRef.current;
        return { x: (px / mW) * rW * zoom + pan.x, y: (py / mH) * rH * zoom + pan.y };
    }, [isCustomUpload, customMapImageRef, dimensions, zoomRef, panRef]);

    if (isAssetsLoading) {
        return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.900' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            ref={containerRef}
            sx={{ width: '100%', height: '100%', position: 'relative', userSelect: 'none', overflow: 'hidden', bgcolor: 'grey.900', cursor: getCursorStyle() }}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => {
                setIsHovering(false); setCursorPos(null);
                setCursorVertex(null); setPathCursorVertex(null);
                setHoveredWorldUid(null); setHoveredPathUid(null);
            }}
        >
            <canvas ref={bgCanvasRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
            <canvas ref={midCanvasRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: (layerVisibility.symbols || activeSection === 'symbols') ? 1 : 0, transition: 'opacity 0.2s' }} />
            <canvas ref={symbolsRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
            <canvas ref={areaCanvasRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', visibility: (activeSection === 'world' || layerVisibility.areas) ? 'visible' : 'hidden' }} />
            <canvas ref={pathCanvasRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
            <canvas ref={labelCanvasRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', visibility: (layerVisibility.labels || activeSection === 'labels') ? 'visible' : 'hidden' }} />
            <canvas ref={gridCanvasRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', visibility: layerVisibility.grid ? 'visible' : 'hidden' }} />
            <canvas
                ref={fgCanvasRef}
                style={{ position: 'absolute', inset: 0, display: 'block' }}
                onMouseDown={(e) => onEvent(e, 'down')}
                onMouseMove={(e) => onEvent(e, 'move')}
                onMouseUp={(e) => onEvent(e, 'up')}
                onMouseLeave={(e) => onEvent(e, 'up')}
                onDoubleClick={(e) => {
                    if (isDrawingArea && activeSection === 'world') { e.preventDefault(); finishDrawing(); }
                    if (isDrawingPath && activeSection === 'paths') {
                        e.preventDefault();
                        let finalSnap: { x: number; y: number } | undefined;
                        if (activePathTypeRef?.current?.pathType === 'river') {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const mx = e.clientX - rect.left;
                            const my = e.clientY - rect.top;
                            const isCustom2 = isCustomUpload && !!customMapImageRef?.current;
                            const mW = isCustom2 ? (customMapImageRef?.current?.naturalWidth ?? BUFFER_SIZE) : BUFFER_SIZE;
                            const mH = isCustom2 ? (customMapImageRef?.current?.naturalHeight ?? BUFFER_SIZE) : BUFFER_SIZE;
                            const { width, height } = dimensions;
                            let rW = Math.min(width, height); let rH = Math.min(width, height);
                            if (isCustom2) {
                                const ratio = mW / mH;
                                if (width / height > ratio) { rH = height; rW = height * ratio; } else { rW = width; rH = width / ratio; }
                            }
                            const bx = ((mx - panRef.current.x) / (rW * zoomRef.current)) * mW;
                            const by = ((my - panRef.current.y) / (rH * zoomRef.current)) * mH;
                            finalSnap = snapRiverNode(bx, by);
                        }
                        finishPathDrawing(finalSnap);
                    }
                }}
                onWheel={onWheel}
                onContextMenu={(e) => e.preventDefault()}
            />

            {/* Entity point pins (HTML overlay, world section only or layer visibility) */}
            {(activeSection === 'world' || layerVisibility.pinpoints) && mapEntityPoints.map(pt => {
                const { x: sx, y: sy } = pointToScreen(pt.x, pt.y);
                const label = pt.label || resolveEntityName(pt.entityRef);
                const isSelected = selectedWorldUid === pt.uid;
                return (
                    <Box
                        key={pt.uid}
                        onClick={() => setSelectedWorldUid(isSelected ? null : pt.uid)}
                        sx={{
                            position: 'absolute', left: sx, top: sy,
                            transform: 'translate(-50%, -100%)', zIndex: 10,
                            cursor: 'pointer', pointerEvents: 'auto',
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            filter: isSelected ? 'drop-shadow(0 0 6px #00a2ff)' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
                            transition: 'filter 0.2s',
                        }}
                    >
                        <Chip
                            label={label}
                            size="small"
                            color={pt.entityRef.type === 'locations' ? 'primary' : 'secondary'}
                            sx={{ fontSize: '0.65rem', height: 20, mb: 0.3, fontWeight: 'bold', opacity: 0.95 }}
                        />
                        <Box sx={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: `8px solid ${pt.entityRef.type === 'locations' ? '#1976d2' : '#9c27b0'}` }} />
                    </Box>
                );
            })}
        </Box>
    );
});

MapCanvas.displayName = 'MapCanvas';
